// Rating-aggregation math — lifted out of PropertyTabs.jsx, where it used
// to be recomputed locally and wasn't available to any other component
// (PropertyHeading, the gallery-adjacent review widget). Single source of
// truth now: called once in PropertyDetails.jsx, passed down as a prop.
// Behavior is unchanged from the original, including the naive
// keyword-based "category" scoring — relocated as-is, not redesigned.
const CATEGORY_KEYWORDS = {
  Hospitality: ["hospitality", "staff", "service", "courtesy", "helpful", "hotel"],
  Facilities: ["facility", "facilities", "amenities"],
  Food: ["food", "breakfast", "meal", "dining"],
  Room: ["room", "bed", "bathroom"],
  Cleanliness: ["clean", "cleanliness", "hygiene"],
};

export function ratingLabel(avg) {
  const value = Number(avg);
  if (value >= 4.5) return "Excellent";
  if (value >= 4.0) return "Very Good";
  if (value >= 3.0) return "Good";
  if (value >= 2.0) return "Average";
  return "Poor";
}

export function computeReviewStats(reviews) {
  const list = reviews || [];
  const totalReviews = list.length;
  const numericRatings = list.map((r) => Number(r.rating));

  const avgRating =
    totalReviews > 0
      ? (numericRatings.reduce((a, b) => a + b, 0) / totalReviews).toFixed(1)
      : "0.0";

  const ratingCount = {
    5: numericRatings.filter((r) => r === 5).length,
    4: numericRatings.filter((r) => r === 4).length,
    3: numericRatings.filter((r) => r === 3).length,
    2: numericRatings.filter((r) => r === 2).length,
    1: numericRatings.filter((r) => r === 1).length,
  };

  const ratingPercentage = (count) =>
    totalReviews ? Math.round((count / totalReviews) * 100) : 0;

  const categoryRatings = {};
  list.forEach((review) => {
    const text = (review.review || "").toLowerCase();
    const ratingValue = Number(review.rating);

    Object.entries(CATEGORY_KEYWORDS).forEach(([category, keywords]) => {
      if (keywords.some((word) => text.includes(word))) {
        if (!categoryRatings[category]) {
          categoryRatings[category] = { total: 0, count: 0 };
        }
        categoryRatings[category].total += ratingValue;
        categoryRatings[category].count += 1;
      }
    });
  });

  const dynamicCategories = Object.entries(categoryRatings)
    .map(([category, data]) => ({
      name: category,
      avg: (data.total / data.count).toFixed(1),
    }))
    .slice(0, 5);

  return {
    totalReviews,
    avgRating,
    ratingCount,
    ratingPercentage,
    ratingLabel: ratingLabel(avgRating),
    dynamicCategories,
  };
}
